/**
 * StatusBanner — top-of-page workflow status card.
 *
 * One full-width card showing:
 *   • Phase headline + a StatusPill for the current step
 *   • Hierarchical-mode chip (with child-TARA count) when applicable
 *   • Counts line (assets · threats · risks · controls · requirements)
 *     — numbers rendered at a larger, bolder size; labels stay
 *     secondary so the eye lands on the figures first.
 *   • Last-activity timestamp on the right
 *
 * Pure presentation — derives nothing. Consumes `data.statusBanner`.
 */
import React from 'react';
import StatusPill from '../../../../components/ui/StatusPill/StatusPill';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import type { AggregatedOverviewData } from '../../aggregate/types';
import styles from './StatusBanner.module.css';

interface StatusBannerProps {
  data: AggregatedOverviewData;
}

const PHASE_HEADLINE: Record<AggregatedOverviewData['statusBanner']['phase'], string> = {
  not_started: 'Workflow not started',
  in_progress: 'Workflow in progress',
  completed: 'Workflow complete',
  failed: 'Workflow failed',
};

const PILL_TONE: Record<AggregatedOverviewData['statusBanner']['phase'], 'info' | 'warning' | 'success' | 'error'> = {
  not_started: 'info',
  in_progress: 'warning',
  completed: 'success',
  failed: 'error',
};

const PILL_LABEL = (phase: AggregatedOverviewData['statusBanner']['phase'], step: number): string => {
  if (phase === 'completed') return 'All steps approved';
  if (phase === 'failed') return `Step ${step} · failed`;
  if (phase === 'not_started') return 'Start with Step 1';
  return `Step ${step} · awaiting review`;
};

const formatRelative = (iso: string | null): string => {
  if (!iso) return 'No activity yet';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'No activity yet';
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'Last activity just now';
  if (mins < 60) return `Last activity ${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Last activity ${hours}h ago`;
  const days = Math.round(hours / 24);
  return `Last activity ${days}d ago`;
};

const STATS: Array<{ key: keyof AggregatedOverviewData['statusBanner']['counts']; singular: string; plural: string }> = [
  { key: 'assets',       singular: 'asset',       plural: 'assets' },
  { key: 'threats',      singular: 'threat',      plural: 'threats' },
  { key: 'risks',        singular: 'risk',        plural: 'risks' },
  { key: 'controls',     singular: 'control',     plural: 'controls' },
  { key: 'requirements', singular: 'requirement', plural: 'requirements' },
];

export default function StatusBanner({ data }: StatusBannerProps) {
  const { phase, currentStep, lastActivity, analysisMode, importedChildTaras, counts } =
    data.statusBanner;

  return (
    <div className={styles.banner}>
      <div className={styles.left}>
        <div className={styles.titleRow}>
          <TypographyH2 className={styles.title}>{PHASE_HEADLINE[phase]}</TypographyH2>
          <StatusPill tone={PILL_TONE[phase]} label={PILL_LABEL(phase, currentStep)} size="sm" />
          {analysisMode === 'hierarchical' && (
            <TypographySmall className={styles.hierarchical}>
              Hierarchical · {importedChildTaras} child TARA{importedChildTaras === 1 ? '' : 's'}
            </TypographySmall>
          )}
        </div>
        <p className={styles.subtitle}>
          {STATS.map((stat, i) => {
            const count = counts[stat.key];
            return (
              <React.Fragment key={stat.key}>
                {i > 0 && <TypographySmall className={styles.statSep}>·</TypographySmall>}
                <span className={styles.statNum}>{count}</span>
                <TypographySmall className={styles.statLabel}>
                  {count === 1 ? stat.singular : stat.plural}
                </TypographySmall>
              </React.Fragment>
            );
          })}
        </p>
      </div>
      <div className={styles.right}>
        <TypographyMuted className={styles.lastActivity}>{formatRelative(lastActivity)}</TypographyMuted>
      </div>
    </div>
  );
}
